import { useEffect, useState, useCallback } from 'react';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { env } from '@diana/env-ui';
import { LOGIN_TYPE_KEY, LoginType } from './useAuthService';

const config: UserManagerSettings = {
  authority: env('CLOUD_AUTHORITY'), // Replace with your OIDC provider URL
  client_id: 'data-logger-ui', // Replace with your client ID
  redirect_uri: `${window.location.protocol}//${window.location.host}/oidc-signin-callback`, // Your app's redirect URI
  response_type: 'code',
  scope: 'openid profile email', // Scopes for user info including email and name
  post_logout_redirect_uri: `${window.location.protocol}//${window.location.host}/oidc-signout-callback`,
  automaticSilentRenew: true,
};

const userManager = new UserManager(config);

export default function useOidcService() {
  const [isAuthenticatedState, setIsAuthenticatedState] = useState<boolean | null>(null); // Null for "undetermined"
  const [user, setUser] = useState<User>();

  const initialize = useCallback(async () => {
    try {
      const currentUser = await userManager.getUser();
      if (currentUser) {
        setIsAuthenticatedState(true);
        setUser(currentUser);
      } else {
        setIsAuthenticatedState(false);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }

    // Set up event listeners
    userManager.events.addSilentRenewError((error) => {
      console.error('Silent renew error while trying to refresh the token', error);
    });

    userManager.events.addAccessTokenExpiring(async () => {
      console.log('Access token expiring, proactively refreshing for SignalR...');
      try {
        await userManager.signinSilent();
      } catch (err) {
        console.error('Proactive token refresh failed:', err);
      }
    });

    userManager.events.addUserLoaded((loadedUser: User) => {
      console.log('User loaded');
      setIsAuthenticatedState(true);
      setUser(loadedUser);
    });

    userManager.events.addUserUnloaded(() => {
      console.log('User unloaded');
      setUser(undefined);
    });

    userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired, removing all authenticated state');
      setIsAuthenticatedState(false);
      setUser(undefined);
    });
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const getIsAuthenticated = useCallback(() => {
    return isAuthenticatedState;
  }, [isAuthenticatedState]);

  const login = useCallback(
    (redirectUrl?: string) => userManager.signinRedirect({ url_state: redirectUrl }),
    [],
  );

  const handleSigninCallback = useCallback(async () => {
    await userManager.signinRedirectCallback();
    const loadedUser = await userManager.getUser();
    if (loadedUser) {
      setIsAuthenticatedState(true);
      localStorage.setItem(LOGIN_TYPE_KEY, LoginType.Oidc);
      setUser(loadedUser);
      return loadedUser;
    } else return Promise.reject();
  }, []);

  const logout = useCallback(async () => await userManager.signoutRedirect(), []);

  const handleSignoutCallback = useCallback(() => userManager.signoutRedirectCallback(), []);

  return {
    user,
    isAuthenticated: getIsAuthenticated(),
    login,
    handleSigninCallback,
    logout,
    handleSignoutCallback,
  };
}
